import { DateTime } from 'luxon';

import type { PrescriptionInfo, ProductInfo } from '@/components/products/refill/ProductRefillCard';
import type { UnavailableProductIssue } from '@/components/products/refill/UnavailableProductRefillCard';
import settings from '@/constants/constants';
import {
  deriveMaxAllowedQuantity,
  getProductFilterTypeFromFormulationId,
  nullToUndefined,
} from '@/hooks/patient/refill/util';
import type { ProductStrain, ProductStrainKey } from '@montugroup/circuit-shared/types/index';

import type { PersistedProductDetails } from '@/hooks/patient/refill/persistedOrderDetails';
import { PRODUCT_ISSUE_TEXT } from '@montugroup/circuit-shared';
import type { ProductCardDetails, RefillResponseProduct } from '@montugroup/circuit-shared/types/refill';
import { shouldShowPIR } from './OrderMedication';

export const PRODUCT_PURCHASE_ISSUE_TEXT = {
  supportPhone: settings.support.phone.display,

  prescriptionExpired: {
    title: PRODUCT_ISSUE_TEXT.PRESCRIPTION_EXPIRED,
    info: ['Book a follow up with your doctor'],
    linkTo: '/patient/consultations',
  },

  outOfRepeats: {
    title: PRODUCT_ISSUE_TEXT.OUT_OF_REPEATS,
    info: ['Book a follow up with your doctor'],
    linkTo: '/patient/consultations',
  },

  outOfStock: {
    title: PRODUCT_ISSUE_TEXT.OUT_OF_STOCK,
    info: ['Check the product status to request a replacement product.'],
  },

  discontinued: {
    title: PRODUCT_ISSUE_TEXT.DISCONTINUED,
    info: ["Please select 'Request replacement' to notify your doctor for a replacement product."],
  },
  infoRequestAlternate: "Please select 'Request alternative product' to notify your doctor for a replacement product.",
  dispenseInterval: {
    title: PRODUCT_ISSUE_TEXT.DISPENSE_INTERVAL,
  },
};

export const UNKNOWN_SUPPLIER_NAME = 'Unknown Supplier';

export const isProductOutOfStock = (product: Pick<RefillResponseProduct, 'is_out_of_stock' | 'reasoning_toggle'>) =>
  Boolean(product.is_out_of_stock || product.reasoning_toggle);

/**
 * For some reason, the Product Refill API response returns either 'remainingUnits' or 'remaining_units'.
 * Which one it returns is inconsistent, so we're normalising it here to snake_case.
 */
export const mapToProductRefillCardProduct = (prescribedProduct: RefillResponseProduct): ProductInfo => ({
  id: prescribedProduct.id,
  cbdForDisplay: prescribedProduct.cbd_strength_for_display || '',
  thcForDisplay: prescribedProduct.thc_strength_for_display || '',
  formulation: getProductFilterTypeFromFormulationId(prescribedProduct.formulation_id),
  price: prescribedProduct.price,
  productName: prescribedProduct.name,
  strainName: prescribedProduct.Strain?.name as ProductStrain,
  strainKey: prescribedProduct.Strain?.shopify_key as ProductStrainKey,
  supplier: prescribedProduct.brand ?? UNKNOWN_SUPPLIER_NAME,
  size: prescribedProduct.size ?? null,
  isConcession: prescribedProduct.is_concession,
  intervalDetails: prescribedProduct.intervalDetails,
});

export const mapToProductCard = (prescribedProduct: RefillResponseProduct | undefined): ProductCardDetails | null => {
  if (prescribedProduct === null || typeof prescribedProduct === 'undefined') {
    return null;
  }
  return {
    id: prescribedProduct.id,
    name: prescribedProduct.name,
    price: prescribedProduct.price,
    brand: prescribedProduct.brand ?? UNKNOWN_SUPPLIER_NAME,
    isOutOfStock: isProductOutOfStock(prescribedProduct),
    reasoningToggle: prescribedProduct.reasoning_toggle,
    isGenerativeScripting: !!prescribedProduct.is_generative_scripting,
    productType: getProductFilterTypeFromFormulationId(prescribedProduct.formulation_id),
    shortName: nullToUndefined(prescribedProduct.short_name),
    thcStrengthForDisplay: nullToUndefined(prescribedProduct.thc_strength_for_display),
    cbdStrengthForDisplay: nullToUndefined(prescribedProduct.cbd_strength_for_display),
    remainingUnits: prescribedProduct.remaining_units!,
    repeats: prescribedProduct.repeats || 0,
    maxAllowedQuantity: deriveMaxAllowedQuantity(prescribedProduct),
    isExpired: prescribedProduct.is_expired ?? undefined,
    prescriptionId: prescribedProduct.prescription_id || 0,
    isCampaignDiscountPrice: prescribedProduct.campaign_discount_price,
    intervalDetails: prescribedProduct.intervalDetails,
  };
};

export const mapToProductRefillCardPrescription = (prescribedProduct: RefillResponseProduct): PrescriptionInfo => ({
  expiry: prescribedProduct.latest_prescription_expiry_date || '',
  maxQuantity: prescribedProduct ? deriveMaxAllowedQuantity(prescribedProduct) : 0,
  maxRepeats: prescribedProduct ? prescribedProduct.repeats || 0 : 0,
  interval: prescribedProduct.interval || 0,
  productSchedule: prescribedProduct?.ProductSchedule,
});

export const formatDateForDisplay = (dateString?: string): string => {
  if (!dateString) {
    return '';
  }
  return DateTime.fromISO(dateString, { setZone: true }).toFormat('d LLL yyyy');
};

export const calculateDaysRemaining = (dateString?: string): number => {
  if (!dateString) {
    return 0;
  }

  const date = DateTime.fromFormat(dateString, 'd LLL yyyy', { setZone: true });

  if (!date.isValid) {
    return 0;
  }

  const today = DateTime.now().startOf('day');
  const diff = date.diff(today, 'days');
  return Math.max(0, Math.floor(diff.days));
};

export const getProductPurchaseIssue = (product: RefillResponseProduct): UnavailableProductIssue | null => {
  const isOutOfStock = product.is_out_of_stock || product.reasoning_toggle;
  const hasNoRemainingUnits = product.remaining_units! <= 0;

  if (product.is_expired) {
    return {
      title: PRODUCT_PURCHASE_ISSUE_TEXT.prescriptionExpired.title,
      info: PRODUCT_PURCHASE_ISSUE_TEXT.prescriptionExpired.info,
      isHighSeverity: false,
    };
  }

  if (hasNoRemainingUnits) {
    return {
      title: PRODUCT_PURCHASE_ISSUE_TEXT.outOfRepeats.title,
      info: PRODUCT_PURCHASE_ISSUE_TEXT.outOfRepeats.info,
      isHighSeverity: false,
    };
  }

  if (isOutOfStock) {
    return {
      title: PRODUCT_PURCHASE_ISSUE_TEXT.outOfStock.title,
      info: shouldShowPIR(product) ? PRODUCT_PURCHASE_ISSUE_TEXT.outOfStock.info : [],
      isHighSeverity: true,
    };
  }

  if (product.is_generative_scripting) {
    return {
      title: PRODUCT_PURCHASE_ISSUE_TEXT.discontinued.title,
      info: [],
      isHighSeverity: true,
    };
  }

  if (product.intervalDetails?.estimatedIntervalState === 'LOCKED') {
    return {
      title: PRODUCT_PURCHASE_ISSUE_TEXT.dispenseInterval.title,
      info: [],
    };
  }

  return null;
};

// Nearest-to-orderable first, matching the relative ordering of the eScript
// page's ISSUE_STATUS_PRIORITY for the reasons that exist on this page.
const UNAVAILABLE_ISSUE_TITLE_ORDER: string[] = [
  PRODUCT_PURCHASE_ISSUE_TEXT.dispenseInterval.title,
  PRODUCT_PURCHASE_ISSUE_TEXT.outOfRepeats.title,
  PRODUCT_PURCHASE_ISSUE_TEXT.prescriptionExpired.title,
  PRODUCT_PURCHASE_ISSUE_TEXT.outOfStock.title,
  PRODUCT_PURCHASE_ISSUE_TEXT.discontinued.title,
];

const getUnavailableIssueRank = (issue: UnavailableProductIssue | null) => {
  const rank = UNAVAILABLE_ISSUE_TITLE_ORDER.indexOf(issue?.title ?? '');
  return rank === -1 ? UNAVAILABLE_ISSUE_TITLE_ORDER.length : rank;
};

export const sortUnavailableProducts = <T extends { issue: UnavailableProductIssue | null }>(pairs: T[]): T[] =>
  [...pairs].sort((a, b) => getUnavailableIssueRank(a.issue) - getUnavailableIssueRank(b.issue));

export const getIssueAndDispenseDateForProduct = (
  product: RefillResponseProduct,
): Pick<PersistedProductDetails, 'intervalState' | 'estimatedNextProcessDate'> => {
  if (product.intervalDetails?.estimatedIntervalState === 'LOCKED') {
    return {
      intervalState: 'LOCKED',
      estimatedNextProcessDate: product.intervalDetails.estimatedNextProcessDate,
    };
  }

  if (product.intervalDetails?.estimatedIntervalState === 'PRE_ORDER') {
    return {
      intervalState: 'PRE_ORDER',
      estimatedNextProcessDate: product.intervalDetails.estimatedNextProcessDate,
    };
  }

  return {
    intervalState: 'AVAILABLE',
    estimatedNextProcessDate: product.intervalDetails?.estimatedNextProcessDate,
  };
};
